import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CalendarDays, ChevronLeft, ChevronRight, CircleAlert, CreditCard, Globe2,
  Mail, MapPin, Package, Phone, RotateCcw, Scale, Table2, Truck,
  UserRound, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import { loadOrder, loadOrderBoard, searchOrders } from './api';
import {
  Empty, Failed, Head, Loading, Pill, RailHeader, SearchField, WorkspaceTabs,
} from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { codeLabel, orderReferenceLabel } from './labels';
import OrdersTable from './OrdersTable';
import ShippingComposer from './ShippingComposer';
import { DispatchStock } from './StoreStock';
import { ShipmentLabelButtons } from './ShippingLabelButton';

const PAYMENT_TONE = {
  succeeded: 'ok', processing: 'info', requires_action: 'warn',
  requires_payment_method: 'warn', requires_confirmation: 'warn',
  requires_capture: 'warn', canceled: 'bad',
};

const SHIPMENT_TONE = {
  created: 'flat', label_printed: 'warn', dispatched: 'info',
  in_transit: 'info', delivered: 'ok', returned: 'warn', lost: 'bad', cancelled: 'flat',
};

// The orders as a calendar or as a table; each person's browser remembers which.
const ORDER_VIEW_KEY = 'xomics.orders.view';

function storedOrderView() {
  try {
    return window.localStorage.getItem(ORDER_VIEW_KEY) === 'table' ? 'table' : 'calendar';
  } catch {
    return 'calendar';
  }
}

const TIMELINE_DAY_PX = 88;
const TIMELINE_LABEL_PX = 210;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function money(minor, currency) {
  if (currency === null || currency === undefined) return '—';
  return `${(Number(minor) / 100).toFixed(2)} ${currency}`;
}

function when(value, options = {}) {
  return new Date(Number(value)).toLocaleDateString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', ...options,
  });
}

function whenTime(value) {
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function customer(order) {
  return `${order.firstName || ''} ${order.lastName || ''}`.trim() || order.email;
}

function addressLines(address) {
  return [
    address.line1,
    address.line2,
    [address.postalCode, address.city, address.region].filter(Boolean).join(' '),
    address.countryCode,
  ].filter(Boolean);
}

function firstOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

// Exactly the month: its first day up to the first day of the next one. Days of
// the neighbouring months are not shown, so stepping a month never lands on a
// day that belongs to another.
function monthWindow(month) {
  return {
    start: new Date(month.getFullYear(), month.getMonth(), 1),
    end: new Date(month.getFullYear(), month.getMonth() + 1, 1),
  };
}

function dayKey(value) {
  const date = value instanceof Date ? value : new Date(Number(value));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function calendarDays(start, end) {
  const days = [];
  for (let date = new Date(start); date < end; date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1)) {
    days.push(date);
  }
  return days;
}

function OrderDetailModal({ uuid, scope, mayManageShip, mayReadShip, onClose, onChanged, onOpenShipment }) {
  const { user } = useXomicsAuth();
  const load = useCallback(() => loadOrder(uuid, { scope }), [scope, uuid]);
  const detail = useLiveResource(['orders', 'shipments'], load);
  // The shipment being marked as dispatched: asked first what it takes from
  // the office's stock, in a form of its own over this one (StoreStock.js).
  const [confirming, setConfirming] = useState(null);

  async function refreshOrder() {
    await detail.refresh();
    onChanged();
  }

  React.useEffect(() => {
    // While the dispatch is being confirmed, Escape leaves that form, not the order.
    if (confirming !== null) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [confirming, onClose]);

  const order = detail.data?.order ?? null;
  const items = detail.data?.items ?? [];
  const payments = detail.data?.payments ?? [];
  const shipments = detail.data?.shipments ?? [];
  const mayFulfilHere = mayManageShip
    && order?.responsibleLocation?.id === user.location.id;
  const total = items.reduce((sum, item) => sum + item.quantity * item.unitPriceMinor, 0);
  const currency = items.length === 0 ? null : items[0].currency;
  const weight = items.reduce(
    (sum, item) => sum + (item.shippingWeightGrams === null ? 0 : item.shippingWeightGrams * item.quantity),
    0,
  );
  const events = order === null ? [] : [
    {
      key: 'placed', at: order.placedAt, kind: 'placed', title: tx("ui.orders.order_placed_bb5d68da"),
      detail: tx("ui.orders.value_items_value2_90bd410a", { value: items.reduce((sum, item) => sum + item.quantity, 0), value2: money(total, currency) }),
    },
    ...payments.map((payment) => ({
      key: `payment-${payment.id}`,
      at: payment.createdAt,
      kind: payment.status === 'succeeded' ? 'paid' : 'payment',
      title: tx("ui.orders.payment_value_6d334f33", { value: codeLabel('paymentStatus', payment.status) }),
      detail: tx("ui.orders.value_provider_b2a56952", { value: money(payment.amountMinor, payment.currency), provider: payment.provider }),
    })),
    ...shipments.flatMap((shipment) => {
      const shipmentEvents = [];
      if (shipment.dispatchedAt === null && shipment.labelUrl) {
        shipmentEvents.push({
          key: `label-${shipment.uuid}`, at: null, kind: 'label',
          title: tx("ui.orders.shipping_label_created_68eb0789"), detail: tx("ui.orders.carrier_trackingnumber_592a9553", { carrier: shipment.carrier, trackingNumber: shipment.trackingNumber }),
        });
      }
      if (shipment.dispatchedAt !== null) {
        shipmentEvents.push({
          key: `dispatch-${shipment.uuid}`, at: shipment.dispatchedAt, kind: 'sent',
          title: tx("ui.orders.order_dispatched_416a840e"), detail: tx("ui.orders.carrier_trackingnumber_592a9553", { carrier: shipment.carrier, trackingNumber: shipment.trackingNumber }),
        });
      }
      if (shipment.deliveredAt !== null) {
        shipmentEvents.push({
          key: `delivery-${shipment.uuid}`, at: shipment.deliveredAt, kind: 'delivered',
          title: tx("ui.orders.order_delivered_a9384cd2"), detail: shipment.trackingNumber,
        });
      }
      return shipmentEvents;
    }),
  ].sort((left, right) => {
    if (left.at === null && right.at === null) return 0;
    if (left.at === null) return 1;
    if (right.at === null) return -1;
    return left.at - right.at;
  });

  return (<>
    <div className="xo-order-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="xo-order-modal" role="dialog" aria-modal="true"
        aria-labelledby="order-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div className="xo-order-modal-title">
            <span className="xo-order-modal-mark" aria-hidden="true"><Package /></span>
            <div>
              {order !== null && <span className="xo-order-modal-kicker">{tx("ui.orders.order_value_e33a2732", { value: orderReferenceLabel(order.trackingCode) })}</span>}
              <h2 id="order-modal-title">{order === null ? tx("ui.orders.order_details_1a897948") : customer(order)}</h2>
              {order !== null && <p>{order.email}</p>}
            </div>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.orders.close_order_details_43432bf9")} onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>

        {detail.loading && detail.data === null && <Loading label={tx("ui.orders.reading_the_order_56a387fa")} />}
        {detail.error && <Failed message={detail.error} onRetry={detail.refresh} />}

        {order !== null && (
          <div className="xo-order-modal-body">
            <div className="xo-order-modal-left">
              <div className="xo-order-facts">
                <span className="xo-order-fact">
                  <CalendarDays aria-hidden="true" />
                  <span><small>{tx("ui.orders.placed_f13a4870")}</small><strong>{whenTime(order.placedAt)}</strong></span>
                </span>
                <span className="xo-order-fact">
                  <CreditCard aria-hidden="true" />
                  <span><small>{tx("ui.orders.order_total_2f32f200")}</small><strong>{money(total, currency)}</strong></span>
                </span>
                <span className="xo-order-fact">
                  <Scale aria-hidden="true" />
                  <span><small>{tx("ui.orders.shipping_weight_9f02d2af")}</small><strong>{weight.toLocaleString()} g</strong></span>
                </span>
              </div>

              <section className="xo-order-detail-section">
                <div className="xo-order-detail-heading">
                  <h3>{tx("ui.orders.customer_and_delivery_02486333")}</h3>
                  <span>{tx("ui.orders.contact_and_destination_13206286")}</span>
                </div>
                <div className="xo-order-contact-grid">
                  <div className="xo-order-contact-card">
                    <header><UserRound aria-hidden="true" /><span>{tx("ui.orders.customer_0e85749a")}</span></header>
                    <strong>{customer(order)}</strong>
                    <span className="xo-order-contact-line"><Mail aria-hidden="true" />{order.email}</span>
                    {order.phone !== null && (
                      <span className="xo-order-contact-line"><Phone aria-hidden="true" />{order.phone}</span>
                    )}
                  </div>
                  <address className="xo-order-contact-card">
                    <header><MapPin aria-hidden="true" /><span>{tx("ui.orders.ship_to_f20e5b4d")}</span></header>
                    <strong>{addressLines(order.address)[0] ?? tx("ui.orders.address_unavailable_5d4d05ac")}</strong>
                    {addressLines(order.address).slice(1).map((line) => <span key={line}>{line}</span>)}
                  </address>
                </div>
              </section>

              <section className="xo-order-detail-section">
                <div className="xo-order-detail-heading">
                  <h3>{tx("ui.orders.items_44d25b5d")}</h3>
                  <span>{tx("ui.orders.value_units_7fa49545", { value: items.reduce((sum, item) => sum + item.quantity, 0) })}</span>
                </div>
                <div className="xo-order-item-list">
                  {items.map((item) => {
                    const imageUrl = item.mainImageThumbnailUrl
                      ?? item.mainImageUrl ?? item.imageUrl ?? null;
                    return (
                      <article className="xo-order-item" key={item.orderItemId}>
                        <div className="xo-order-product-cell">
                          <span className="xo-order-product-image">
                            {imageUrl === null
                              ? <Package aria-hidden="true" />
                              : <img src={imageUrl} alt="" width="52" height="52"
                                  loading="lazy" decoding="async" />}
                          </span>
                          <span className="xo-lines">
                            <strong>{item.name}</strong>
                            {item.configuration?.label && (
                              <span className="xo-order-item-choice">{item.configuration.label}</span>
                            )}
                            <span>{item.configuration?.sku ?? item.code} · {codeLabel('productType', item.itemType)}</span>
                          </span>
                        </div>
                        <dl className="xo-order-item-values">
                          <div><dt>{tx("ui.orders.quantity_44f6af69")}</dt><dd>{item.quantity}</dd></div>
                          <div><dt>{tx("ui.orders.weight_69c0b815")}</dt><dd>{item.shippingWeightGrams === null ? '—' : tx("ui.orders.value_g_79d2b4ba", { value: (item.shippingWeightGrams * item.quantity).toLocaleString() })}</dd></div>
                          <div><dt>{tx("ui.orders.line_total_7fd7c1ce")}</dt><dd>{money(item.quantity * item.unitPriceMinor, item.currency)}</dd></div>
                        </dl>
                      </article>
                    );
                  })}
                </div>
              </section>

              <div className="xo-order-detail-columns">
                <section className="xo-order-detail-section">
                  <h3>{tx("ui.orders.payments_44357ae5")}</h3>
                  {payments.length === 0 ? <p className="xo-muted">{tx("ui.orders.no_payment_recorded_eee6b8c4")}</p> : payments.map((payment) => (
                    <div className="xo-order-detail-record" key={payment.id}>
                      <div><strong>{money(payment.amountMinor, payment.currency)}</strong><Pill tone={PAYMENT_TONE[payment.status]}>{codeLabel('paymentStatus', payment.status)}</Pill></div>
                      <span><small>{tx("ui.orders.provider_7ceee3f3")}</small>{payment.provider}</span>
                      <span><small>{tx("ui.orders.recorded_d5383ea7")}</small>{whenTime(payment.createdAt)}</span>
                      {payment.providerPaymentId && <span><small>{tx("ui.orders.reference_db1c7845")}</small><span className="xo-mono">{payment.providerPaymentId}</span></span>}
                      {payment.refundedMinor > 0 && <span>{tx("ui.orders.refunded_value_4f4cbc25", { value: money(payment.refundedMinor, payment.currency) })}</span>}
                      {payment.failureMessage && <span className="t-bad">{payment.failureMessage}</span>}
                    </div>
                  ))}
                </section>

                <section className="xo-order-detail-section">
                  <h3>{tx("ui.orders.shipments_7a70be8a")}</h3>
                  {shipments.length === 0 ? <p className="xo-muted">{tx("ui.orders.no_shipping_label_created_86ec0cb6")}</p> : shipments.map((shipment) => (
                    <div className="xo-order-detail-record" key={shipment.uuid}>
                      <div><strong>{shipment.carrier}</strong><Pill tone={SHIPMENT_TONE[shipment.status]}>{codeLabel('shipmentStatus', shipment.status)}</Pill></div>
                      <span><small>{tx("ui.orders.tracking_e81a151b")}</small><span className="xo-mono">{shipment.trackingNumber}</span></span>
                      {shipment.dispatchedAt !== null && <span><small>{tx("ui.orders.dispatched_ff07201a")}</small>{whenTime(shipment.dispatchedAt)}</span>}
                      {shipment.deliveredAt !== null && <span><small>{tx("ui.orders.delivered_eea956cd")}</small>{whenTime(shipment.deliveredAt)}</span>}
                      {shipment.box && <span><small>{tx('ui.carrier.box')}</small>{shipment.box.name}</span>}
                      {shipment.labels.filter((label) => label.direction === 'return').map((label) => (
                        <span className="xo-return-line" key={`return-${label.sequence}`}>
                          <RotateCcw aria-hidden="true" /> {tx('ui.carrier.return_tracking_n', { n: label.sequence })} {label.trackingNumber
                            ? <span className="xo-mono">{label.trackingNumber}</span> : tx('ui.carrier.return_with_outbound')}
                        </span>
                      ))}
                      {mayReadShip && <ShipmentLabelButtons labels={shipment.labels} className="xo-order-label-action" />}
                      {mayReadShip && (
                        <button type="button" className="xo-link xo-order-open-shipment"
                          onClick={() => onOpenShipment(shipment.uuid)}>
                          <Truck aria-hidden="true" />
                          {tx('ui.samples.open_shipment')}
                          <ChevronRight aria-hidden="true" />
                        </button>
                      )}
                      {mayFulfilHere && shipment.status === 'label_printed' && shipment.dispatchedAt === null && (
                        <button type="button" className="xo-btn xo-order-dispatch"
                          disabled={confirming !== null} onClick={() => setConfirming(shipment.uuid)}>
                          <Truck aria-hidden="true" />
                          {tx('ui.carrier.mark_dispatched')}
                        </button>
                      )}
                    </div>
                  ))}
                  {mayFulfilHere && shipments.length === 0 && (
                    <ShippingComposer subject={{ subjectType: 'order', subjectUuid: order.uuid }} onDone={refreshOrder} />
                  )}
                </section>
              </div>
            </div>

            <aside className="xo-order-event-panel">
              <header>
                <div><span>{tx("ui.orders.order_activity_3a98ef61")}</span><h3>{tx("ui.orders.timeline_018514a3")}</h3></div>
                <span>{tx("ui.orders.length_events_1d30de9d", { length: events.length })}</span>
              </header>
              <ol>
                {events.map((event) => (
                  <li key={event.key}>
                    <span className={`xo-order-event-dot t-${event.kind}`} aria-hidden="true" />
                    <div>
                      <strong>{event.title}</strong>
                      <span>{event.detail}</span>
                      <time>{event.at === null ? tx("ui.orders.time_not_recorded_a5cb1f29") : whenTime(event.at)}</time>
                    </div>
                  </li>
                ))}
              </ol>
            </aside>
          </div>
        )}
      </section>
    </div>
    {confirming !== null && (
      <DispatchStock shipmentUuid={confirming} onClose={() => setConfirming(null)}
        onDispatched={() => { setConfirming(null); refreshOrder(); }} />
    )}
  </>);
}

function TimelineRow({ days, gridStyle, indexByDay, order, shipments, today, onOpen }) {
  const lastIndex = days.length - 1;
  const startIndex = Math.max(0, Math.min(
    lastIndex,
    indexByDay.get(dayKey(order.placedAt)) ?? 0,
  ));
  const latestShipment = shipments.length === 0 ? null : shipments[shipments.length - 1];
  const terminalAt = latestShipment?.dispatchedAt ?? Date.now();
  const terminalKey = dayKey(terminalAt);
  let endIndex = indexByDay.get(terminalKey);
  if (endIndex === undefined) {
    endIndex = Number(terminalAt) < days[0].getTime() ? 0 : lastIndex;
  }
  endIndex = Math.max(startIndex, Math.min(lastIndex, endIndex));
  const label = latestShipment === null
    ? tx('ui.orders.placed_awaiting_dispatch', { placed: when(order.placedAt) })
    : tx('ui.orders.placed_last_dispatched', {
      placed: when(order.placedAt), dispatched: when(latestShipment.dispatchedAt),
    });
  const milestones = new Map([[startIndex, [{
    key: 'placed', kind: 'placed', at: order.placedAt,
  }]]]);
  shipments.forEach((shipment) => {
    const shipmentIndex = indexByDay.get(dayKey(shipment.dispatchedAt));
    if (shipmentIndex === undefined) return;
    const atDay = milestones.get(shipmentIndex) ?? [];
    atDay.push({
      key: shipment.shipmentUuid,
      kind: 'shipped',
      at: shipment.dispatchedAt,
    });
    milestones.set(shipmentIndex, atDay);
  });

  return (
    <div className="xo-timeline-row" style={gridStyle}>
      <button type="button" className="xo-timeline-order" onClick={() => onOpen(order.uuid)}>
        <strong>{customer(order)}</strong>
        <span>{orderReferenceLabel(order.trackingCode)} · {order.address.city}</span>
      </button>
      {days.map((date, index) => (
        <span key={dayKey(date)} aria-hidden="true"
          className={`xo-timeline-cell${dayKey(date) === today ? ' is-today' : ''}`}
          style={{ gridColumn: index + 2 }} />
      ))}
      <button type="button"
        className={`xo-order-strip${latestShipment === null ? ' is-open' : ''}`}
        style={{ gridColumn: `${startIndex + 2} / ${endIndex + 3}` }}
        title={tx("ui.orders.value_label_f38454eb", { value: customer(order), label: label })}
        aria-label={tx("ui.orders.value_label_ee2e16b3", { value: customer(order), label: label })}
        onClick={() => onOpen(order.uuid)}>
        {latestShipment === null && (
          <span className="xo-order-strip-alert" aria-hidden="true">
            <CircleAlert />
          </span>
        )}
      </button>
      {[...milestones.entries()].map(([index, events]) => (
        <button type="button" className="xo-order-milestones"
          key={`${order.uuid}-${index}`} style={{ gridColumn: index + 2 }}
          aria-label={events.length === 1
            ? tx('ui.orders.one_order_event_on_date', { date: when(days[index]) })
            : tx('ui.orders.many_order_events_on_date', { count: events.length, date: when(days[index]) })}
          onClick={() => onOpen(order.uuid)}>
          {events.map((event) => (
            <span key={event.key}
              className={`xo-order-milestone t-${event.kind}`}
              title={event.kind === 'placed'
                ? tx('ui.orders.placed_on_date', { date: when(event.at) })
                : tx('ui.orders.dispatched_on_date', { date: when(event.at) })}>
              {event.kind === 'placed'
                ? <Package aria-hidden="true" />
                : <Truck aria-hidden="true" />}
            </span>
          ))}
        </button>
      ))}
    </div>
  );
}

export default function XomicsOrders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = useXomicsAuth();
  const mayManageShip = permissions.includes('SHIPMENT_MANAGE');
  const mayReadShip = permissions.includes('SHIPMENT_READ');
  const mayReadGlobal = permissions.includes('ORDER_READ_ALL');
  const [locationScope, setLocationScope] = useState(
    () => (mayReadGlobal && searchParams.get('scope') === 'global' ? 'global' : 'local'),
  );
  // The calendar shows one month at a time. The rail's unsent orders do not
  // follow it: an order still to send is listed whichever month is open.
  const [month, setMonth] = useState(() => firstOfMonth(new Date()));
  const [todayRequest, setTodayRequest] = useState(0);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [orderTab, setOrderTab] = useState('unsent');
  const [view, setView] = useState(storedOrderView);
  const timelineRef = React.useRef(null);
  const linkedOrderUuid = useMemo(() => {
    const value = String(searchParams.get('orderUuid') ?? '').trim();
    return UUID_RE.test(value) ? value : null;
  }, [searchParams]);

  React.useEffect(() => {
    if (!mayReadGlobal && locationScope !== 'local') setLocationScope('local');
  }, [locationScope, mayReadGlobal]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const dateWindow = useMemo(() => monthWindow(month), [month]);
  const rangeStart = dateWindow.start.getTime();
  const rangeEnd = dateWindow.end.getTime();
  const load = useCallback(() => loadOrderBoard({
    from: rangeStart, to: rangeEnd, scope: locationScope,
  }), [locationScope, rangeEnd, rangeStart]);
  const board = useLiveResource(['orders', 'shipments'], load);
  const searchActive = debouncedQuery.length >= 2;
  const loadSearch = useCallback(() => searchOrders({
    query: debouncedQuery, status: orderTab, limit: 50, scope: locationScope,
  }), [debouncedQuery, locationScope, orderTab]);
  const search = useLiveResource(['orders', 'shipments'], loadSearch, { enabled: searchActive });
  const searchReady = search.data?.query === debouncedQuery
    && search.data?.scope === locationScope && search.data?.status === orderTab;
  const days = useMemo(
    () => calendarDays(new Date(rangeStart), new Date(rangeEnd)),
    [rangeEnd, rangeStart],
  );
  const today = dayKey(new Date());
  const indexByDay = useMemo(
    () => new Map(days.map((date, index) => [dayKey(date), index])),
    [days],
  );
  const gridStyle = useMemo(() => ({
    gridTemplateColumns: `var(--xo-timeline-label) repeat(${days.length}, var(--xo-timeline-day))`,
  }), [days.length]);
  const boardReady = board.data !== null;

  React.useEffect(() => {
    if (linkedOrderUuid !== null) setSelected(linkedOrderUuid);
  }, [linkedOrderUuid]);

  function closeSelectedOrder() {
    setSelected(null);
    if (linkedOrderUuid === null) return;
    const next = new URLSearchParams(searchParams);
    next.delete('orderUuid');
    setSearchParams(next, { replace: true });
  }

  function openShipment(shipmentUuid) {
    const params = new URLSearchParams({ shipmentUuid });
    if (locationScope === 'global') params.set('scope', 'global');
    setSelected(null);
    navigate(`/xomics/shipments?${params.toString()}`);
  }

  function changeLocationScope(next) {
    setLocationScope(next);
    setSelected(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('orderUuid');
    if (next === 'global') nextParams.set('scope', 'global');
    else nextParams.delete('scope');
    setSearchParams(nextParams, { replace: true });
  }

  function stepMonth(step) {
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + step, 1));
  }

  // Today opens this month and brings today's column back to the middle, also
  // when this month is already open and the calendar was scrolled away from it.
  function showToday() {
    setMonth(firstOfMonth(new Date()));
    setTodayRequest((count) => count + 1);
  }

  const monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  // This month lands with today's column centred. Any other month opens on its
  // first day.
  React.useEffect(() => {
    if (!boardReady || timelineRef.current === null) return undefined;
    const todayIndex = indexByDay.get(today);
    const frame = window.requestAnimationFrame(() => {
      const scroller = timelineRef.current;
      if (scroller === null) return;
      if (todayIndex === undefined) {
        scroller.scrollTo({ left: 0, behavior: 'auto' });
        return;
      }
      const targetCentre = TIMELINE_LABEL_PX + (todayIndex * TIMELINE_DAY_PX) + (TIMELINE_DAY_PX / 2);
      scroller.scrollTo({
        left: Math.max(0, targetCentre - (scroller.clientWidth / 2)),
        behavior: 'smooth',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [boardReady, indexByDay, rangeStart, today, todayRequest]);

  const shipmentsByOrder = useMemo(() => {
    const grouped = new Map();
    if (board.data === null) return grouped;
    board.data.dispatched.forEach((shipment) => {
      const shipments = grouped.get(shipment.orderUuid) ?? [];
      shipments.push(shipment);
      grouped.set(shipment.orderUuid, shipments);
    });
    grouped.forEach((shipments) => shipments.sort((a, b) => a.dispatchedAt - b.dispatchedAt));
    return grouped;
  }, [board.data]);
  const timelineOrders = useMemo(() => {
    if (board.data === null) return [];
    return board.data.placed.map((order) => ({
      order,
      shipments: shipmentsByOrder.get(order.uuid) ?? [],
    })).sort((left, right) => {
      const leftSent = left.shipments.length > 0 ? 1 : 0;
      const rightSent = right.shipments.length > 0 ? 1 : 0;
      if (leftSent !== rightSent) return leftSent - rightSent;
      return left.order.placedAt - right.order.placedAt || left.order.id - right.order.id;
    });
  }, [board.data, shipmentsByOrder]);
  const unsentOrders = useMemo(() => {
    if (board.data === null) return [];
    return board.data.unsent.map((order) => ({ order, shipments: [] }))
      .sort((left, right) => left.order.placedAt - right.order.placedAt
        || left.order.id - right.order.id);
  }, [board.data]);
  const calendarSentOrders = useMemo(
    () => timelineOrders.filter(({ shipments }) => shipments.length > 0),
    [timelineOrders],
  );
  const sentOrders = calendarSentOrders;

  function changeView(next) {
    setView(next);
    try {
      window.localStorage.setItem(ORDER_VIEW_KEY, next);
    } catch {
      // A browser that refuses storage opens on the calendar next time.
    }
  }

  const headTabs = mayReadGlobal && (
    <WorkspaceTabs className="is-compact xo-order-location-tabs" value={locationScope}
      label={tx('ui.orders.location_scope')} onChange={changeLocationScope}
      tabs={[
        { value: 'local', label: tx('ui.samples.local_dc99d54d'), Icon: MapPin },
        { value: 'global', label: tx('ui.samples.global_5f1184f7'), Icon: Globe2 },
      ]} />
  );
  // How the orders are shown belongs with them: at the end of the calendar's bar and of the table's.
  const viewSwitch = (
    <div className="xo-order-view" role="group" aria-label={tx('ui.orders.table.view_label')}>
      {[
        ['calendar', CalendarDays, tx('ui.orders.table.view_calendar')],
        ['table', Table2, tx('ui.orders.table.view_table')],
      ].map(([value, Icon, label]) => (
        <button type="button" key={value} className={view === value ? 'is-on' : ''} aria-pressed={view === value}
          aria-label={label} title={label} onClick={() => changeView(value)}>
          <Icon aria-hidden="true" />
        </button>
      ))}
    </div>
  );
  const orderModal = selected !== null && (
    <OrderDetailModal uuid={selected} scope={locationScope} mayManageShip={mayManageShip}
      mayReadShip={mayReadShip} onClose={closeSelectedOrder}
      onChanged={board.refresh} onOpenShipment={openShipment} />
  );

  if (view === 'table') {
    return (
      <>
        <div className="xo-orders-table-page">
          <Head title={tx("ui.orders.orders_cded0933")} subtitle={tx("ui.orders.placed_orders_dispatches_and_labels_waiting_to_be_crea_8cf19328")}>
            {headTabs}
          </Head>
          <OrdersTable scope={locationScope} onOpen={setSelected} viewSwitch={viewSwitch} />
        </div>
        {orderModal}
      </>
    );
  }

  if (board.loading && board.data === null) return <Loading label={tx("ui.orders.reading_the_order_calendar_7afcd827")} />;
  if (board.error) return <Failed message={board.error} onRetry={board.refresh} />;
  if (board.data === null) return null;

  const defaultSideOrders = orderTab === 'unsent' ? unsentOrders : sentOrders;
  const sideOrders = searchActive
    ? (searchReady ? search.data.orders.map((order) => ({ order, shipments: [] })) : [])
    : query.trim().length === 1 ? [] : defaultSideOrders;
  // While another month is being read the board still holds the month before it.
  const monthLoaded = Number(board.data.range?.from) === rangeStart;

  return (
    <>
      <div className="xo-order-board">
        <main className="xo-orders-main">
          <Head title={tx("ui.orders.orders_cded0933")} subtitle={tx("ui.orders.placed_orders_dispatches_and_labels_waiting_to_be_crea_8cf19328")}>
            {headTabs}
          </Head>

          <section className="xo-order-calendar">
            <header className="xo-calendar-bar">
              <strong aria-live="polite">{monthLabel}</strong>
              <div className="xo-calendar-step">
                <button type="button" className="xo-btn-icon" onClick={() => stepMonth(-1)}
                  aria-label={tx('ui.collections.previous')} title={tx('ui.collections.previous')}>
                  <ChevronLeft aria-hidden="true" />
                </button>
                <button type="button" className="xo-btn-2" onClick={showToday}>
                  {tx('ui.mobile.today')}
                </button>
                <button type="button" className="xo-btn-icon" onClick={() => stepMonth(1)}
                  aria-label={tx('ui.collections.next')} title={tx('ui.collections.next')}>
                  <ChevronRight aria-hidden="true" />
                </button>
                {viewSwitch}
              </div>
            </header>
            <div className="xo-timeline-frame">
              <div className="xo-timeline-scroll" ref={timelineRef}>
                <div className="xo-timeline">
                  <div className="xo-timeline-header" style={gridStyle}>
                    <span className="xo-timeline-corner">{tx("ui.orders.order_1d75774c")}</span>
                    {days.map((date, index) => (
                      <span key={dayKey(date)}
                        className={`xo-timeline-date${dayKey(date) === today ? ' is-today' : ''}`}
                        style={{ gridColumn: index + 2 }}>
                        <small>{date.toLocaleDateString(undefined, { weekday: 'short' })}</small>
                        <strong>{date.getDate()}</strong>
                        <span>{date.toLocaleDateString(undefined, { month: 'short' })}</span>
                      </span>
                    ))}
                  </div>
                  {monthLoaded && timelineOrders.map(({ order, shipments }) => (
                    <TimelineRow key={order.uuid} days={days} gridStyle={gridStyle}
                      indexByDay={indexByDay} order={order} shipments={shipments}
                      today={today} onOpen={setSelected} />
                  ))}
                  {/* Under the last order the day columns run on to the bottom of the calendar. */}
                  {monthLoaded && timelineOrders.length > 0 && (
                    <div className="xo-timeline-row xo-timeline-fill" style={gridStyle} aria-hidden="true">
                      <span className="xo-timeline-fill-label" />
                      {days.map((date, index) => (
                        <span key={dayKey(date)}
                          className={`xo-timeline-cell${dayKey(date) === today ? ' is-today' : ''}`}
                          style={{ gridColumn: index + 2 }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* A month without orders is a plain panel, its message centred on what is on screen. */}
              {!monthLoaded ? (
                <div className="xo-timeline-note">
                  <Loading label={tx("ui.orders.reading_the_order_calendar_7afcd827")} />
                </div>
              ) : timelineOrders.length === 0 && (
                <div className="xo-timeline-note">
                  <Empty title={tx("ui.orders.no_orders_in_this_period_f029b9fb")} detail={tx("ui.orders.try_another_month_4ec46046")} />
                </div>
              )}
            </div>
            <footer className="xo-calendar-key">
              <span><Package className="t-placed" aria-hidden="true" /> {tx("ui.orders.order_placed_bb5d68da")}</span>
              <span><Truck className="t-sent" aria-hidden="true" /> {tx("ui.orders.dispatched_ff07201a")}</span>
            </footer>
          </section>
        </main>

        <aside className="xo-orders-rail">
          <RailHeader title={tx("ui.orders.order_activity_3a98ef61")} />
          <WorkspaceTabs className="xo-order-tabs is-rail" value={orderTab}
            label={tx("ui.orders.order_status_22edd6c1")} onChange={setOrderTab}
            tabs={[
              { value: 'unsent', label: tx("ui.orders.unsent_0d09fa0d"), count: board.data.unsentTotal, Icon: Package },
              { value: 'sent', label: tx("ui.orders.sent_35f49dcf"), count: calendarSentOrders.length, Icon: Truck },
            ]} />
          <div className="xo-workspace-search">
            <SearchField value={query} onChange={setQuery}
              placeholder={tx("ui.orders.search_orders_7ab30154")} />
          </div>

          <div className="xo-orders-side-list" role="tabpanel">
            {searchActive && !searchReady && !search.error ? (
              <Loading label={tx("ui.orders.search_orders_7ab30154")} />
            ) : searchActive && search.error ? (
              <Failed message={search.error} onRetry={search.refresh} />
            ) : sideOrders.length === 0 ? (
              <Empty title={tx("ui.orders.no_ordertab_orders_cde2e41a", { orderTab: orderTab })} detail={query.trim() === '' ? tx("ui.orders.nothing_to_show_here_0409a92a") : tx("ui.orders.try_another_search_1824b924")} />
            ) : sideOrders.map(({ order, shipments }) => {
              const latestShipment = shipments[shipments.length - 1] ?? null;
              return (
                <article className="xo-orders-side-order" key={order.uuid}>
                  <button type="button" className="xo-order-side-open" onClick={() => setSelected(order.uuid)}>
                    <span className="xo-order-side-copy">
                      <span className="xo-order-side-topline">
                        <strong>{customer(order)}</strong>
                        <span className="xo-mono">{orderReferenceLabel(order.trackingCode)}</span>
                      </span>
                      <span>{order.unitCount === 1
                        ? tx('ui.orders.one_item_total', { total: money(order.totalMinor, order.currency) })
                        : tx('ui.orders.many_items_total', { count: order.unitCount, total: money(order.totalMinor, order.currency) })}</span>
                      <small>{tx("ui.orders.placed_value_444815e6", { value: when(order.placedAt) })}</small>
                      <span className="xo-order-side-actions">
                        {orderTab === 'sent'
                          ? <Pill tone="ok">{tx("ui.orders.dispatched_ff07201a")}</Pill>
                          : order.labelCount > 0
                            ? <Pill tone="warn">{tx("ui.orders.label_printed_6225c56f")}</Pill>
                            : <Pill tone="flat">{tx("ui.orders.no_label_d756b1f6")}</Pill>}
                        {orderTab === 'sent' && latestShipment !== null && (
                          <span className="xo-order-sent-meta">
                            {when(latestShipment.dispatchedAt)} · {latestShipment.carrier.name}
                          </span>
                        )}
                      </span>
                    </span>
                    <ChevronRight className="xo-order-side-chevron" aria-hidden="true" />
                  </button>
                </article>
              );
            })}
          </div>
          {orderTab === 'unsent' && !searchActive && board.data.unsentTotal > board.data.unsent.length && (
            <footer>{tx("ui.orders.showing_length_of_unsenttotal_unsent_orders_309191fb", { length: unsentOrders.length, unsentTotal: board.data.unsentTotal })}</footer>
          )}
          {orderTab === 'sent' && searchActive && searchReady && (
            <footer>{tx("ui.orders.length_matching_sent_orders_in_this_calendar_74d29019", { length: sideOrders.length })}</footer>
          )}
        </aside>
      </div>

      {orderModal}
    </>
  );
}
